//
// Copyright (c) 2021 Astute Global, Inc.
//

import { Plugin, toWidget, viewToModelPositionOutsideModelElement, Widget } from "ckeditor5";
import { ResponseAttributeCommand } from "./responseAttributeCommand";
import { PLACEHOLDER_VALUE } from "../ckEditorConstants";

export default class ResponseAttributeEditing extends Plugin {
  // noinspection JSUnusedGlobalSymbols
  static get requires() {
    return [Widget];
  }

  // noinspection JSUnusedGlobalSymbols
  static get pluginName() {
    return "ResponseAttributeEditing";
  }

  init() {
    const editor = this.editor;
    this._setupConfig();
    this._setupSchema();
    this._setupConversions();
    editor.commands.add("responseAttribute", new ResponseAttributeCommand(editor));
    editor.editing.mapper.on(
      "viewToModelPosition",
      viewToModelPositionOutsideModelElement(editor.model, (viewElement) =>
        viewElement.hasClass("responseAttribute"),
      ),
    );
  }

  _createConversationElement(modelItem, writer, allowPlaceholder) {
    const insertType = modelItem.getAttribute("insertType");
    const attributeId = modelItem.getAttribute("attributeId");
    if (attributeId == PLACEHOLDER_VALUE && !allowPlaceholder) {
      return null;
    }
    const attributeName = modelItem.getAttribute("attributeName");
    const element = writer.createContainerElement("span", {
      class: "attributeinsert",
      "data-akinserttype": insertType,
      "data-akattribute": attributeId,
    });
    const text = writer.createText(attributeName);
    writer.insert(writer.createPositionAt(element, 0), text);
    return element;
  }

  _setupConfig() {
    this.editor.config.define("responseAttribute", {
      emptyAttributeName: "Please select an attribute.",
    });
  }

  _setupConversions() {
    const conversion = this.editor.conversion;
    conversion.for("upcast").elementToElement({
      view: {
        name: "span",
        attributes: ["data-akattribute", "data-akinserttype"],
        classes: ["attributeinsert"],
      },
      model: (viewElement, { writer }) => {
        const insertType = viewElement.getAttribute("data-akinserttype");
        const attributeId = viewElement.getAttribute("data-akattribute");
        const attributeName = viewElement.getChild(0).data;
        return writer.createElement("responseAttribute", {
          insertType,
          attributeId,
          attributeName,
        });
      },
    });
    conversion.for("dataDowncast").elementToElement({
      model: "responseAttribute",
      view: (modelItem, { writer }) => this._createConversationElement(modelItem, writer),
    });
    conversion.for("editingDowncast").elementToElement({
      model: "responseAttribute",
      view: (modelItem, { writer }) => {
        const element = this._createConversationElement(modelItem, writer, true);
        if (!element) {
          return null;
        }
        return toWidget(element, writer);
      },
    });
  }

  _setupSchema() {
    this.editor.model.schema.register("responseAttribute", {
      allowWhere: "$text",
      inheritTypesFrom: "$text",
      isObject: true,
      allowAttributes: ["insertType", "attributeId", "attributeName"],
    });
  }
}
